import React from 'react'
import { SwiperSlide } from 'swiper/react'
import { number, array, string, object, bool } from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { getImageOrDefaultSrc, urlWithTrack } from '../../helpers/common'
import { sendClickEventToInsights } from '../../utils/insights'
import { NUMERAL, ZERO, EMPTY_ARRAY } from '../../utils/constants'
import Image from '../ui/image/Image'
import AdSwiper from '../ui/ad-swiper/AdSwiper'
import AdLink from '../ui/ad-link/AdLink'
import SaveButton from '../ui/button/SaveButton'
import Insights from './Insights'
import { manufacturerMessages as messages } from './messages'

const swiperParams = {
  slidesPerView: 'auto',
  freeMode: true
}

const Company = ({
  bimFilesCount = ZERO,
  categories,
  description = '',
  image,
  insights,
  linkedProjectsCount = ZERO,
  products = EMPTY_ARRAY,
  title,
  url = NUMERAL,
  type,
  gridView
}) => {
  const handleLinkClick = () => {
    sendClickEventToInsights(insights.category, insights.label, insights.value, insights.version)
  }
  const trackedUrl = urlWithTrack(url, type)

  const productsLength = products.length
  const productsShorted = products.slice(0, 5)
  const saveButtonImage = productsShorted.length > 0 ? productsShorted[0].featured_image : image

  return (
    <Insights {...insights}>
      <div className='object-container company-object'>
        {/* Image */}
        <div className='object-image company-object__image-container'>
          <p className='company-object__available-products'>
            <span className='object-content__bold'>
              {productsLength} <FormattedMessage {...messages.availableProducts} />
              {':'}
            </span>
          </p>
          {products.length > 1 ? (
            <AdSwiper isLazy params={swiperParams}>
              {!!image && (
                <SwiperSlide lazy='true'>
                  <div className='company-object__logo-wrapper'>
                    <AdLink onClick={handleLinkClick} href={trackedUrl}>
                      {/* eslint-disable-next-line max-len */}
                      <Image className='company-object__logo swiper-lazy' src={image.url_medium} data-object-fit='cover' loading='lazy' />
                    </AdLink>
                  </div>
                </SwiperSlide>
              )}
              {productsShorted.map((product, index) => (
                <div key={index} className='company-object__image-wrapper'>
                  {index === productsShorted.length - 1 ? (
                    <AdLink
                      className='gallery-link'
                      href={urlWithTrack(url + '/#products-box', type)}
                      style={{ backgroundImage: `url(${getImageOrDefaultSrc(product.featured_image)})` }}
                      onClick={handleLinkClick}
                    >
                      <span className='gallery-link__overlay' />
                      <span className='gallery-link__content'>
                        <span className='afd-table-display afd-table-display--center'>
                          <span className='gallery-link__size afd-cell-display'>{'+ ' + productsLength}</span>
                        </span>
                      </span>
                    </AdLink>
                  ) : (
                    <AdLink href={urlWithTrack(product.url, type)} onClick={handleLinkClick}>
                      <Image className='swiper-lazy' data-src={product.featured_image} data-object-fit='cover' />
                      <h5 className='company-object__image-title '>{product.title}</h5>
                    </AdLink>
                  )}
                </div>
              ))}
            </AdSwiper>
          ) : (
            <div className='company-object__logo-wrapper'>
              <AdLink href={trackedUrl} onClick={handleLinkClick}>
                <Image className='company-object__logo' src={image.url_medium} data-object-fit='cover' crop={false} />
              </AdLink>
            </div>
          )}
          <SaveButton title={title} image={saveButtonImage} url={url} gridView={gridView} insights={insights} />
        </div>

        {/* title */}
        <h3 className='object-title article-object__title afd-title-big'>
          <AdLink className='afd-title--black-link' href={trackedUrl} onClick={handleLinkClick}>
            {title}
          </AdLink>
        </h3>

        {/* metadata */}
        <ul className='object-metadata'>
          <li>
            <FormattedMessage {...messages.manufacturer} />
          </li>
        </ul>

        {/* content */}
        <div className='object-content article-object-content'>
          {/* description */}
          {categories.length < 1 &&
            productsShorted < 1 && <p>{description.length > 200 ? description.substr(0, 196) + '...' : description}</p>}
          {/* categories */}
          {categories.length > 0 && (
            <p>
              <span className='object-content__bold'>
                <FormattedMessage {...messages.productCategories} />
                {': '}
              </span>
              {categories.slice(0, 4).map((item, index) => (
                <AdLink key={index} href={urlWithTrack(item.url, type)}>
                  {!!index && ', '}
                  {item.name}
                </AdLink>
              ))}
            </p>
          )}

          {/* Available Products */}
          {productsShorted.lenght > 0 && (
            <p className='company-object__content-available-products'>
              <span className='object-content__bold'>
                <FormattedMessage {...messages.availableProducts} />
                {': '}
              </span>
              {productsShorted.slice(0, 2).map((item, index) => (
                <AdLink key={index} href={urlWithTrack(item.url, type)}>
                  {item.title}
                  {index < productsLength - 1 && ', '}
                </AdLink>
              ))}
              {productsLength > 2 && <AdLink href={urlWithTrack(url + '/#products-box', type)}>{`+ ${productsLength}`}</AdLink>}
            </p>
          )}

          {/* Linked projects count */}
          {!!linkedProjectsCount && (
            <p>
              <span className='object-content__bold'>
                <FormattedMessage {...messages.linkedProjects} />
                {': '}
              </span>
              <AdLink href={urlWithTrack(url + '/#products-box', type)}>{linkedProjectsCount}</AdLink>
            </p>
          )}

          {/* BIM files count */}
          {!!bimFilesCount && (
            <p>
              <span className='object-content__bold'>
                <FormattedMessage {...messages.bimObjects} />
                {': '}
              </span>
              <AdLink href={urlWithTrack(url + '/bim-files', type)}>{bimFilesCount}</AdLink>
            </p>
          )}
        </div>
      </div>
    </Insights>
  )
}

Company.propTypes = {
  bimFilesCount: number,
  categories: array,
  linkedProjectsCount: number,
  products: array,
  title: string,
  url: string,
  description: string,
  image: object,
  insights: object,
  type: string,
  gridView: bool
}

export default React.memo(Company)
